import { useTranslation } from 'react-i18next'

import { EditSolidIcon, PlusIcon } from '@dhl-ui/icons'
import { useGetOrganisations, type OrganisationDto } from '@dhl/mdp-api'
import { type GridColDef, Pagination } from '@dhl/components'
import { Box, Button, Grid, IconButton, Paper } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { AdminDataGrid, SearchTextField, useNavigateWithSearchString, useSearchAndPage } from '~/common'

export const OrganisationsPage = () => {
  const { t } = useTranslation()
  const navigateWithSearchString = useNavigateWithSearchString()
  const { searchAndPageParams, handleSearch, page, pageSize, setPage, setPageSize, search } = useSearchAndPage()

  const { data, isFetching } = useGetOrganisations(searchAndPageParams)

  const handleRowClick = (row: OrganisationDto) => {
    navigateWithSearchString(links.organisationDetailsLink(row?.id), search)
  }

  const renderCellEdit = (row: OrganisationDto) => {
    return (
      <IconButton
        aria-label="edit organisation details"
        onClick={() => navigateWithSearchString(links.editOrganisationLink(row.id.toString()))}
      >
        <EditSolidIcon data-testid="edit-organisation-details" data-test-id={row?.id.toString()} />
      </IconButton>
    )
  }

  const columns: GridColDef<OrganisationDto>[] = [
    {
      field: 'name',
      headerName: t('admin.organisationsPage.organisationOverview.name'),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: t('admin.organisationsPage.organisationOverview.actions'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => renderCellEdit(row),
    },
  ]

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        data-testid="organisations-page"
      >
        <Grid>
          <SearchTextField onChange={handleSearch} />
        </Grid>
        <Grid>
          <Button
            variant="contained"
            color="primary"
            data-testid="add-organisation"
            sx={{ alignSelf: 'flex-end' }}
            startIcon={<PlusIcon />}
            onClick={() => navigateWithSearchString(links.addOrganisationLink())}
          >
            {t('admin.organisationsPage.addNewOrganisation')}
          </Button>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            loading={isFetching}
            data-testid="organisation-table"
            autoHeight
            rows={data?.content ?? []}
            columns={columns}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={data?.totalCount ?? 0}
            onPageChange={(_, page) => {
              setPage(page)
            }}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(pageSize) => {
              setPageSize(pageSize)
            }}
          />
        </Box>
      </Box>
    </>
  )
}
